import React, { useState, useEffect, useCallback } from 'react';
const API_BASE_URL = "https://final-project-5ar7.onrender.com";

const EMPTY = { label: '', network: 'TRC20', walletAddress: '', minimumDeposit: 100, estimatedCreditTime: '6 minutes', isActive: false, allowAllUsers: true, note: '' };

export default function CryptoSettings() {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrFile, setQrFile] = useState({});
  const [uploadingQr, setUploadingQr] = useState({});

  const fetchGateways = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/public/crypto-gateways`);
      const data = await res.json();
      if (data.success) setGateways(data.gateways);
    } catch (e) { console.error('❌ fetchCryptoGateways:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGateways(); }, [fetchGateways]);

  const handleSave = async () => {
    if (!form.label || !form.walletAddress) { alert('Label and Wallet Address are required'); return; }
    setSaving(true);
    try {
      const url = editId
        ? `${API_BASE_URL}/api/admin/public/crypto-gateways/${editId}`
        : `${API_BASE_URL}/api/admin/public/crypto-gateways`;
      const res = await fetch(url, { method: editId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) { setForm(EMPTY); setEditId(null); fetchGateways(); }
      else alert('Error: ' + data.message);
    } catch (e) { alert('Network error'); }
    setSaving(false);
  };

  const handleUploadQr = async (id) => {
    const file = qrFile[id];
    if (!file) return alert('Select a QR image first');
    setUploadingQr(prev => ({ ...prev, [id]: true }));
    const fd = new FormData();
    fd.append('qrCode', file);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/public/crypto-gateways/${id}/upload-qr`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) { fetchGateways(); setQrFile(prev => ({ ...prev, [id]: null })); }
      else alert('Upload failed: ' + data.message);
    } catch (e) { alert('Upload error'); }
    setUploadingQr(prev => ({ ...prev, [id]: false }));
  };

  const handleToggle = async (gw) => {
    await fetch(`${API_BASE_URL}/api/admin/public/crypto-gateways/${gw._id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...gw, isActive: !gw.isActive }) });
    fetchGateways();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this crypto gateway?')) return;
    await fetch(`${API_BASE_URL}/api/admin/public/crypto-gateways/${id}`, { method: 'DELETE' });
    fetchGateways();
  };

  const card = { background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' };
  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const lbl = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 };

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: '0 auto' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>₿ Crypto USDT Settings</h1>
      <p style={{ color: '#6b7280', marginBottom: 32 }}>Manage crypto wallet addresses, QR codes and network settings shown to users during deposits.</p>

      {/* ADD / EDIT FORM */}
      <div style={card}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{editId ? '✏️ Edit Gateway' : '➕ Add New Crypto Gateway'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div><label style={lbl}>Label *</label><input style={inp} placeholder="e.g. USDT Main Wallet" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} /></div>
          <div><label style={lbl}>Network *</label><input style={inp} placeholder="e.g. TRC20" value={form.network} onChange={e => setForm({ ...form, network: e.target.value })} /></div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={lbl}>Wallet Address *</label>
          <input style={inp} placeholder="e.g. TJ8f1x2W2X3Y4g7r8u9i0oP1q2R3s4t5u6" value={form.walletAddress} onChange={e => setForm({ ...form, walletAddress: e.target.value })} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <div><label style={lbl}>Minimum Deposit ($)</label><input style={inp} type="number" value={form.minimumDeposit} onChange={e => setForm({ ...form, minimumDeposit: parseFloat(e.target.value) })} /></div>
          <div><label style={lbl}>Estimated Credit Time</label><input style={inp} placeholder="e.g. 6 minutes" value={form.estimatedCreditTime} onChange={e => setForm({ ...form, estimatedCreditTime: e.target.value })} /></div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={lbl}>Note for User (optional)</label>
          <input style={inp} placeholder="e.g. Send only USDT to this address" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
        </div>

        {/* QR upload only shows when editing */}
        {editId && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
            <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 14, color: '#1e40af' }}>📷 Upload / Replace QR Code</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input type="file" accept="image/*" onChange={e => setQrFile(prev => ({ ...prev, [editId]: e.target.files[0] }))} style={{ fontSize: 13 }} />
              <button onClick={() => handleUploadQr(editId)} disabled={uploadingQr[editId]}
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                {uploadingQr[editId] ? '⏳ Uploading...' : '⬆ Upload QR'}
              </button>
              {gateways.find(g => g._id === editId)?.qrCodeUrl && (
                <img src={gateways.find(g => g._id === editId).qrCodeUrl} alt="Current QR"
                  style={{ width: 60, height: 60, borderRadius: 6, border: '2px solid #3b82f6', objectFit: 'cover' }} />
              )}
            </div>
            <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 12 }}>💡 Save the gateway first, then upload a QR code here or from the card below.</p>
          </div>
        )}
        {!editId && (
          <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px dashed #d1d5db', marginTop: 16 }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>📷 <strong>QR Code:</strong> Click <strong>"Add Gateway"</strong> first, then upload the QR image from the card that appears below.</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={form.allowAllUsers} onChange={e => setForm({ ...form, allowAllUsers: e.target.checked })} /> Allow All Users
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ background: '#1fa84f', color: '#fff', border: 'none', padding: '11px 28px', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: 15 }}>
            {saving ? 'Saving...' : editId ? '✅ Update Gateway' : '➕ Add Gateway'}
          </button>
          {editId && (
            <button onClick={() => { setEditId(null); setForm(EMPTY); }}
              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '11px 24px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* EXISTING GATEWAYS */}
      {loading ? <p>Loading...</p> : gateways.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: '#9ca3af', padding: 48 }}>
          <p style={{ fontSize: 18 }}>No crypto gateways configured yet.</p>
        </div>
      ) : gateways.map(gw => (
        <div key={gw._id} style={{ ...card, borderLeft: `4px solid ${gw.isActive ? '#f59e0b' : '#e5e7eb'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{gw.label}</h3>
                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{gw.network}</span>
                <span style={{ background: gw.isActive ? '#d1fae5' : '#f3f4f6', color: gw.isActive ? '#065f46' : '#6b7280', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{gw.isActive ? '🟢 Active' : '🔴 Inactive'}</span>
              </div>
              <p style={{ margin: '0 0 2px', fontSize: 13, color: '#374151', fontFamily: 'monospace' }}>{gw.walletAddress}</p>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Min: ${gw.minimumDeposit} · Credit: {gw.estimatedCreditTime}</p>
            </div>
            {gw.qrCodeUrl && (
              <img src={gw.qrCodeUrl} alt="QR" style={{ width: 90, height: 90, borderRadius: 8, border: '2px solid #e5e7eb', objectFit: 'cover', marginLeft: 20 }} />
            )}
          </div>

          {/* QR Upload per card */}
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>📷 {gw.qrCodeUrl ? 'Replace QR:' : 'Upload QR:'}</span>
            <input type="file" accept="image/*" onChange={e => setQrFile(prev => ({ ...prev, [gw._id]: e.target.files[0] }))} style={{ fontSize: 13 }} />
            <button onClick={() => handleUploadQr(gw._id)} disabled={uploadingQr[gw._id]}
              style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {uploadingQr[gw._id] ? 'Uploading...' : '⬆ Upload'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={() => handleToggle(gw)}
              style={{ background: gw.isActive ? '#fef3c7' : '#d1fae5', color: gw.isActive ? '#92400e' : '#065f46', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              {gw.isActive ? '⏸ Deactivate' : '▶ Activate'}
            </button>
            <button onClick={() => { setEditId(gw._id); setForm({ label: gw.label, network: gw.network, walletAddress: gw.walletAddress, minimumDeposit: gw.minimumDeposit, estimatedCreditTime: gw.estimatedCreditTime, isActive: gw.isActive, allowAllUsers: gw.allowAllUsers, note: gw.note || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              style={{ background: '#eff6ff', color: '#1d4ed8', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              ✏️ Edit Details
            </button>
            <button onClick={() => handleDelete(gw._id)}
              style={{ background: '#fef2f2', color: '#dc2626', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🗑 Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}